import { DataStore, DataStoreKey } from '@compiere-ws/models/compiere-data-json';
import SelectOrderComponent from '@iupics-components/specific/window/select-order/select-order.component';
import GridViewUiComponent from '@iupics-components/standard/grid/grid-view-ui/grid-view-ui.component';
import { LogicEvaluator } from '@iupics-util/tools/logic-evaluator';
import { cloneDeep, has } from 'lodash';
import EditTabUiComponent from '../../edit-tab-ui/edit-tab-ui.component';
import EditViewUiComponent from '../edit-view-ui.component';

export class EditViewUtils {
  /**
   *
   * @param {string}displayLogic
   * @param {Map<string, any>}dataMap
   * @returns {boolean} displayCss
   */
  static isTabDisplay(displayLogic: string, dataMap: Map<string, any>): boolean {
    if (displayLogic) {
      return LogicEvaluator.evaluateLogic(dataMap, displayLogic);
    }
    return true;
  }

  /**
   * Find the index of the current tabId in the container
   * @param {any}container
   * @param {number}tabId
   * @returns {number} index
   */
  static findMyIndex(container: any, tabId: number): number {
    let find = false;
    let myIndex = -1;
    let i = 1;
    while (!find && container.DOMChildrenComponent.length > i) {
      if (container.DOMChildrenComponent[i].tabId === tabId) {
        myIndex = i;
        find = true;
      }
      i++;
    }
    return myIndex;
  }

  /**
   *
   * @param {string}value
   */
  static copyToClipboard(value: string) {
    const el = document.createElement('textarea');
    el.value = value;
    el.setAttribute('readonly', '');
    // Move outside the screen to make it invisible
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    // Check if there is any content selected previously, Store selection if found, Mark as false to know no selection existed before
    const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false;
    // Select the <textarea> content
    el.select();
    // Copy - only works as a result of a user action (e.g. click events)
    // eslint-disable-next-line deprecation/deprecation
    document.execCommand('copy');
    document.body.removeChild(el);
    if (selected) {
      document.getSelection().removeAllRanges();
      document.getSelection().addRange(selected);
    }
  }

  /**
   *
   * @param {EditTabUiComponent}tab
   * @param {DataStore}dataStore
   * @param {any}container
   * @param {number}tabId
   */
  static updateEditViewData(tab: EditTabUiComponent, dataStore: DataStore, container: any, tabId: number) {
    if (tab.DOMChildrenComponent[0] instanceof SelectOrderComponent) {
      (tab.DOMChildrenComponent[0] as SelectOrderComponent).init();
    } else {
      tab.updateAllLogic(dataStore);
      if (tab.displayCss === 'inline') {
        if (
          container.DOMChildrenComponent[EditViewUtils.findMyIndex(container, tabId) + 1] &&
          tab.tabId ===
            container.DOMChildrenComponent[EditViewUtils.findMyIndex(container, tabId) + 1].DOMComponent.instance.tabId
        ) {
          (
            tab.DOMChildrenComponent[0].DOMComponent.instance as GridViewUiComponent
          ).GridTabInfinityScrollUiComponent.createServerDataSource(
            dataStore.data,
            EditViewUtils.findMyIndex(container, tabId) + 1
          );
        } else {
          if (
            (tab.DOMChildrenComponent[0].DOMComponent.instance as GridViewUiComponent)
              .GridTabInfinityScrollUiComponent !== undefined
          ) {
            (
              tab.DOMChildrenComponent[0].DOMComponent.instance as GridViewUiComponent
            ).GridTabInfinityScrollUiComponent.createServerDataSource(dataStore.data);
          }
        }
      }
    }
  }

  /**
   *
   * @param {EditTabUiComponent}tab
   * @param {DataStore}dataStore
   * @param {any}container
   * @param {number}tabId
   */
  static checkDisplayTabCollapse(tab: EditTabUiComponent, dataMap: Map<string, any>, displayLogic) {
    if (tab.DOMChildrenComponent[0] instanceof SelectOrderComponent) {
      (tab.DOMChildrenComponent[0] as SelectOrderComponent).init();
    } else {
      if (EditViewUtils.isTabDisplay(displayLogic, dataMap)) {
        tab.displayCss = 'inline';
      } else {
        tab.displayCss = 'none';
      }
    }
  }

  /**
   *
   * @param {any}container
   * @param {number}tabId
   * @param {EditTabUiComponent[]}editTabs
   */
  static updateOtherEditView(container: any, tabId: number, editTabs: EditTabUiComponent[], dataStored: DataStore) {
    const indexEditViewToEdit = EditViewUtils.findMyIndex(container, tabId) + 1;
    const editView = container.DOMChildrenComponent?.[indexEditViewToEdit];
    if (!editView) {
      return;
    }

    let find = false;
    let i = 1;
    while (!find && i < editTabs.length) {
      if (editTabs[i].tabId === editView.DOMComponent.instance.tabId) {
        find = true;
      } else {
        i++;
      }
    }

    if (find && (editTabs[i].DOMComponent.instance.displayCss === 'inline' || !editTabs[i].data.isCollapsable)) {
      editView.DOMComponent.instance.noData = false;
    } else if (
      editView instanceof EditViewUiComponent &&
      !editView.DOMComponent.instance.isZoomEditView &&
      !editView.DOMComponent.instance.isZoomTarget
    ) {
      editView.DOMComponent.instance.removeComponent(true);
    } else {
      let j = 0;
      while (j < container.DOMChildrenComponent.length) {
        if (
          container.DOMChildrenComponent[j] instanceof EditViewUiComponent &&
          container.DOMChildrenComponent[j].DOMComponent.instance.isZoomTarget &&
          dataStored &&
          dataStored.key.recordId !== container.DOMChildrenComponent[j].DOMComponent.instance.zoomTarget.record_id
        ) {
          container.DOMChildrenComponent[j].DOMComponent.instance.removeComponent(true);
        }

        j++;
      }
    }
  }

  /**
   *
   * @param {string}logic
   * @param {Map<string, any>}dataMap
   * @returns {boolean}
   */
  static checkLogic(logic: string, dataMap: Map<string, any>): boolean {
    if (logic) {
      return LogicEvaluator.evaluateLogic(dataMap, logic);
    }

    return false;
  }

  /**
   *
   * @param edit EditView sur lequel on veut récupérer la clé du parent
   */
  static getParentDatastoreKey(edit: EditViewUiComponent): DataStoreKey | undefined {
    return edit?.linkedComponents?.[0]?.editViewParent?.currentDataStoreKey ?? undefined;
  }

  /**
   *
   * @param editTab EditTab sur lequel on veut récupérer la clé du parent
   */
  static getParentDatastoreKeyFromTab(editTab: EditTabUiComponent): DataStoreKey | undefined {
    return editTab?.editViewParent?.linkedComponents?.[0]?.currentDataStoreKey ?? undefined;
  }

  /**
   *
   * @param grid GridView sur lequel on veut récupérer la clé du parent
   */
  static getParentDatastoreKeyFromGrid(grid: GridViewUiComponent): DataStoreKey | undefined {
    return grid?.editViewParent?.currentDataStoreKey ?? undefined;
  }

  /**
   * Permet de merge les dataToAdd avec le currentData
   * @param childData data du store sur lequel on veut ajouter des data
   * @param parentData data du store que l'on veut ajouter
   * @param isDefaultValue va servir pour overrider la valeur du parent même si celui dans l'enfant est undefined
   *  (Exemple: Description sur C_Order et C_Orderline)
   */
  static mergeCurrentDataDeepCopy(childData: any, parentData: any, overrideChild = true, forceErase = false): any {
    if (parentData) {
      const parentKeys = Object.keys(parentData);
      for (const key of parentKeys) {
        let shouldOverrideChild = false;
        if (!forceErase) {
          if (overrideChild) {
            shouldOverrideChild =
              parentData?.[key] !== undefined &&
              parentData?.[key] !== null &&
              (childData?.[key] === undefined || childData?.[key] === null);
          } else {
            shouldOverrideChild = !has(childData, key);
          }
        } else {
          shouldOverrideChild = true;
        }

        if (shouldOverrideChild) {
          try {
            childData[key] = cloneDeep(parentData[key]);
          } catch {
            childData[key] = undefined;
          }
        }
      }
    }
    // Suppression apiz_dataResult inutile dans le contexte
    if (childData?.['apiz_dataResult']) {
      delete childData['apiz_dataResult'];
    }
    if (childData?.['apiz_ctxChanged']) {
      delete childData['apiz_ctxChanged'];
    }

    return childData;
  }

  static getCurrentContext(
    editView?: EditViewUiComponent,
    store?: DataStore,
    userContext?: any,
    overrideChild = true
  ): any {
    let parentStoreData = {};
    let currentParent = editView;
    if (currentParent && currentParent.editTabs && currentParent.editTabs[0].dataStored.data) {
      /*on initialise le contexte avec le store du premier parent */
      parentStoreData = EditViewUtils.mergeCurrentDataDeepCopy(
        parentStoreData,
        currentParent.editTabs[0].dataStored.data,
        overrideChild,
        true
      );
    }
    if (currentParent && currentParent.isZoomEditView) {
      while (currentParent && currentParent.isZoomEditView) {
        if (
          currentParent.linkedComponents &&
          currentParent.linkedComponents[0] &&
          currentParent.linkedComponents[0].editViewParent &&
          currentParent.linkedComponents[0].editViewParent.isZoomEditView &&
          currentParent.linkedComponents[0].editViewParent.editTabs[0].dataStored.data
        ) {
          /*on merge le store parent courant avec le contexte */
          parentStoreData = EditViewUtils.mergeCurrentDataDeepCopy(
            parentStoreData,
            currentParent.linkedComponents[0].editViewParent.editTabs[0].dataStored.data,
            overrideChild
          );
          currentParent = currentParent.linkedComponents[0].editViewParent;
        } else {
          currentParent = null;
        }
      }
    } else {
      while (currentParent) {
        if (
          currentParent.linkedComponents &&
          currentParent.linkedComponents[0] &&
          currentParent.linkedComponents[0].editViewParent &&
          currentParent.linkedComponents[0].editViewParent.editTabs[0].dataStored.data
        ) {
          if (currentParent?.isZoomEditView) {
            currentParent = null;
          } else {
            /*on merge le store parent courant avec le contexte */
            parentStoreData = EditViewUtils.mergeCurrentDataDeepCopy(
              parentStoreData,
              currentParent.linkedComponents[0].editViewParent.editTabs[0].dataStored.data,
              overrideChild
            );
            currentParent = currentParent.linkedComponents[0].editViewParent;
          }
        } else {
          currentParent = null;
        }
      }
    }

    let data = {};
    /*On ajoute le contexte utilisateur */
    if (userContext) {
      const keys = Object.keys(userContext);
      for (const key of keys) {
        if (key.includes('|') || key.startsWith('$') || key.startsWith('#')) {
          data[key] = cloneDeep(userContext[key]);
        }
      }
    }
    /*on merge le contexte avec le store du composant concerné et le contexte utilisateur */
    let output = {};
    data = EditViewUtils.mergeCurrentDataDeepCopy(data, store ? store.data : {}, overrideChild);
    if (data['AD_Org_ID'] !== undefined && data['AD_Org_ID'] !== null) {
      data['#AD_Org_ID'] = data['AD_Org_ID'] ? data['AD_Org_ID'].id : parseInt(data['AD_Org_ID'], 10);
    }
    output = EditViewUtils.mergeCurrentDataDeepCopy(data, cloneDeep(parentStoreData), overrideChild);

    // Suppression apiz_dataResult inutile dans le contexte
    if (data && has(data, 'apiz_dataResult')) {
      delete data['apiz_dataResult'];
    }
    if (data && has(data, 'apiz_ctxChanged')) {
      delete data['apiz_ctxChanged'];
    }
    return output;
  }
}
